import * as React from "react";
import { Menu, MenuItem } from "@blueprintjs/core";
import type { ItemListRendererProps } from "@blueprintjs/select";

const MAX_RENDERED_ITEMS = 100;

/**
 * Renders at most MAX_RENDERED_ITEMS filtered menu items, with a disabled
 * "N more" hint when the rest were hidden. Slicing before renderItem also
 * caps Blueprint QueryList's O(n²) matchesPredicate indexOf work.
 *
 * Because this supplies a custom itemListRenderer, it supersedes Blueprint's
 * noResults / initialContent / create-item list rendering props — callers
 * should omit those (this renderer hardcodes the empty "No results." row).
 *
 * Known limitation: QueryList arrow-key navigation still walks the full
 * filtered list, so the active-item highlight disappears past the cap and
 * Enter can select an unrendered item. The "keep typing to narrow" hint
 * mitigates this by encouraging a smaller filtered set.
 *
 * Implemented with React.createElement (not JSX) so Jest can import this
 * module under the Emotion css-prop Babel preset.
 */
export function cappedItemListRenderer<T>(
  listProps: ItemListRendererProps<T>
) {
  const { filteredItems, renderItem, itemsParentRef, menuProps } = listProps;
  const visible = filteredItems.slice(0, MAX_RENDERED_ITEMS);
  const rendered = visible.map(renderItem).filter((el) => el != null);
  const hiddenCount = filteredItems.length - visible.length;

  return React.createElement(
    Menu,
    { role: "listbox", ...menuProps, ulRef: itemsParentRef },
    rendered.length > 0
      ? rendered
      : React.createElement(MenuItem, {
          disabled: true,
          text: "No results.",
          roleStructure: "listoption",
        }),
    hiddenCount > 0 &&
      React.createElement(MenuItem, {
        disabled: true,
        roleStructure: "listoption",
        text: `${hiddenCount} more — keep typing to narrow`,
      })
  );
}
